// FollowUpsPage — /follow-ups, the staff call list (docs/follow-ups-plan.md §3).
//
// Leases a resident has not signed and rental references a previous landlord
// has not answered, 24 hours after they were sent — who to call, at what
// number, about what. Rendered OUTSIDE AppShell, in FollowUpsShell, because
// anyone at welcomehps.com may open this page while the builder may be
// admin-only (§5); nothing here links back into the builder.
//
// This half fetches and polls; FollowUpsTable renders. The list refreshes
// every 5 minutes while someone is using the tab (usePollWhileActive: paused
// when hidden or idle, caught up on return — an unattended tab must not keep
// the Aurora cluster awake). Every action (snooze, unsnooze, mark done, put
// back) answers with the whole refreshed list, which replaces what is on screen
// at once; no second GET.
//
// WEL-181: a reference row can be cancelled. The table only reports the key;
// this page opens the confirm, posts the cancel (lib/followUpsReference.ts —
// strict about an HTTP 200 that says `failed`), shows a refusal INSIDE the
// confirm with the server's reason (never "done"), and on success drops the row
// at once and reloads the list.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { formatWhen } from '@/lib/commandCenterClient';
import {
  fetchFollowUps,
  markFollowUpDone,
  putBackFollowUp,
  snoozeFollowUp,
  unsnoozeFollowUp,
  type FollowUpDoneReason,
  type FollowUpsResponse,
  type SnoozeDays,
} from '@/lib/followUpsClient';
import { usePollWhileActive } from '@/hooks/usePollWhileActive';
import { toast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { cancelRequests } from '@/lib/requestsClient';
import {
  CANCEL_REFERENCE_BUSY_TEXT,
  CANCEL_REFERENCE_CONFIRM_TEXT,
  REFERENCE_CANCELLED_TEXT,
  cancelReference,
  cancelReferenceCopy,
  findFollowUp,
  landlordForConfirm,
  withoutFollowUp,
} from '@/lib/followUpsReference';
import { OcError, OcLoading } from '@/components/oc/bits';
import { FollowUpsShell } from '@/components/followUps/FollowUpsShell';
import { FollowUpsTable, shortDate, shortDateTime } from '@/components/followUps/FollowUpsTable';

/** How often an in-use tab re-reads the list. */
export const FOLLOW_UPS_POLL_MS = 5 * 60_000;

function bodyMessage(err: ApiError): string | null {
  const body = err.body as { error?: string; message?: string } | null;
  return body?.message ?? body?.error ?? null;
}

function loadError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return 'This list is for Welcome Home Properties staff. Sign in with your welcomehps.com account.';
    }
    return bodyMessage(err) ?? `Couldn't load the follow-ups (${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't load the follow-ups.";
}

function actionError(err: unknown, verb: string): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'That one is no longer on the list — it was signed, answered or closed.';
    if (err.status === 403) return 'This list is for Welcome Home Properties staff.';
    return bodyMessage(err) ?? `Couldn't ${verb} (${err.status}).`;
  }
  return err instanceof Error ? err.message : `Couldn't ${verb}.`;
}

export function FollowUpsPage(): React.JSX.Element {
  const [data, setData] = useState<FollowUpsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);
  // WEL-181: the reference row whose Cancel is being confirmed, the landlord it
  // names (read when the confirm opened, so a reload that drops the row cannot
  // change the words), and the refusal to show inside the confirm.
  const [cancelTarget, setCancelTarget] = useState<{ key: string; landlord: string } | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // A poll that was already in flight when a snooze answered carries the list
  // from BEFORE the snooze; applying it would put the row back until the next
  // poll. `epoch` moves whenever a mutation starts or finishes, and a read only
  // lands when nothing moved while it was out.
  const mutationsRef = useRef({ inFlight: 0, epoch: 0 });

  const load = useCallback(async () => {
    const startedAt = mutationsRef.current.epoch;
    try {
      const res = await fetchFollowUps();
      if (!aliveRef.current) return;
      const m = mutationsRef.current;
      if (m.inFlight > 0 || m.epoch !== startedAt) return;
      if (res.degraded?.length) {
        // An incomplete read must not replace a complete list with a shorter
        // one; say so and keep what was last shown (or show the partial list
        // when there is nothing else).
        setData((prev) => prev ?? res);
        setError('Some records could not be read just now, so the list may be incomplete.');
        return;
      }
      setData(res);
      setError(null);
    } catch (err) {
      if (aliveRef.current) setError(loadError(err));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  usePollWhileActive(() => {
    void load();
  }, FOLLOW_UPS_POLL_MS);

  const mutate = useCallback(
    async (
      key: string,
      verb: string,
      run: () => Promise<FollowUpsResponse>,
      done: (res: FollowUpsResponse) => string,
    ) => {
      const m = mutationsRef.current;
      m.inFlight += 1;
      m.epoch += 1;
      setBusyKey(key);
      setMutating(true);
      let refetch = false;
      try {
        const res = await run();
        if (!aliveRef.current) return;
        setData(res);
        setError(null);
        toast.success(done(res));
      } catch (err) {
        if (!aliveRef.current) return;
        toast.error(actionError(err, verb));
        // Gone from the list (signed, answered, closed): show that now rather
        // than at the next poll.
        refetch = err instanceof ApiError && err.status === 404;
      } finally {
        m.inFlight -= 1;
        m.epoch += 1;
        if (aliveRef.current) {
          setBusyKey((k) => (k === key ? null : k));
          setMutating(m.inFlight > 0);
        }
      }
      if (refetch) void load();
    },
    [load],
  );

  const onSnooze = useCallback(
    (key: string, days: SnoozeDays) => {
      void mutate(
        key,
        'snooze it',
        () => snoozeFollowUp(key, days),
        (res) => {
          const until = shortDate(res.snoozed.find((i) => i.key === key)?.snooze?.until);
          return until ? `Snoozed until ${until}.` : `Snoozed for ${days === 1 ? '1 day' : `${days} days`}.`;
        },
      );
    },
    [mutate],
  );

  const onUnsnooze = useCallback(
    (key: string) => {
      void mutate(key, 'unsnooze it', () => unsnoozeFollowUp(key), () => 'Unsnoozed.');
    },
    [mutate],
  );

  const onDone = useCallback(
    (key: string, reason: FollowUpDoneReason) => {
      void mutate(
        key,
        'mark it done',
        () => markFollowUpDone(key, reason),
        (res) => {
          if (reason === 'not_needed') return 'Marked not needed.';
          const back = shortDateTime(res.done?.find((i) => i.key === key)?.done?.until);
          return back ? `Marked called. Back on the list ${back} if still waiting.` : 'Marked called.';
        },
      );
    },
    [mutate],
  );

  const onPutBack = useCallback(
    (key: string) => {
      void mutate(key, 'put it back', () => putBackFollowUp(key), () => 'Back on the list.');
    },
    [mutate],
  );

  const onCancelReference = useCallback(
    (key: string) => {
      const item = data ? findFollowUp(data, key) : undefined;
      if (!item) return;
      setCancelError(null);
      setCancelTarget({ key, landlord: landlordForConfirm(item) });
    },
    [data],
  );

  const closeCancel = useCallback(() => {
    setCancelTarget(null);
    setCancelError(null);
  }, []);

  const confirmCancelReference = useCallback(async () => {
    if (!cancelTarget) return;
    const { key } = cancelTarget;
    const m = mutationsRef.current;
    m.inFlight += 1;
    m.epoch += 1;
    setBusyKey(key);
    setMutating(true);
    setCancelling(true);
    setCancelError(null);
    let cancelled = false;
    try {
      await cancelReference(key, cancelRequests);
      cancelled = true;
    } catch (err) {
      if (aliveRef.current) setCancelError(actionError(err, 'cancel the reference'));
    } finally {
      m.inFlight -= 1;
      m.epoch += 1;
      if (aliveRef.current) {
        setBusyKey((k) => (k === key ? null : k));
        setMutating(m.inFlight > 0);
        setCancelling(false);
      }
    }
    if (!aliveRef.current) return;
    if (cancelled) {
      // Drop the row now (so it does not linger if the reload is slow or fails),
      // then reload for the server's list.
      setData((prev) => (prev ? withoutFollowUp(prev, key) : prev));
      setCancelTarget(null);
      toast.success(REFERENCE_CANCELLED_TEXT);
    }
    // After a refusal too: the reference may have been answered or closed in
    // the meantime, and the list behind the confirm should say so.
    void load();
  }, [cancelTarget, load]);

  const refresh = () => {
    if (!data) setLoading(true);
    void load();
  };

  return (
    <FollowUpsShell>
      {/* A call sheet, not a dashboard: cap the width so five columns do not
          sprawl across a wide monitor. */}
      <div className="oc-page" style={{ maxWidth: 1440, marginInline: 'auto' }}>
        <header className="oc-page-header">
          <span className="oc-meta">
            Leases a resident has not signed and rental references a previous landlord has not
            answered, 24 hours after they were sent.
            {data?.generatedAt ? ` As of ${formatWhen(data.generatedAt)}.` : ''}
          </span>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            onClick={refresh}
          >
            Refresh
          </button>
        </header>

        {error && (
          <OcError>
            {error}
            {data ? ' Showing the list as it last loaded.' : ''}
          </OcError>
        )}

        {data ? (
          <FollowUpsTable
            due={data.due}
            snoozed={data.snoozed}
            // `?? []`: an API from before done marks sends no `done` list.
            done={data.done ?? []}
            onSnooze={onSnooze}
            onUnsnooze={onUnsnooze}
            onDone={onDone}
            onPutBack={onPutBack}
            onCancelReference={onCancelReference}
            busyKey={busyKey}
            mutating={mutating}
          />
        ) : loading ? (
          <OcLoading label="Loading follow-ups…" />
        ) : null}
      </div>
      <ConfirmDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open && !cancelling) closeCancel();
        }}
        title={cancelReferenceCopy(cancelTarget?.landlord ?? '').title}
        description={cancelReferenceCopy(cancelTarget?.landlord ?? '').description}
        confirmLabel={CANCEL_REFERENCE_CONFIRM_TEXT}
        busyLabel={CANCEL_REFERENCE_BUSY_TEXT}
        tone="danger"
        busy={cancelling}
        error={cancelError}
        onConfirm={() => void confirmCancelReference()}
      />
    </FollowUpsShell>
  );
}
